import React, { useState } from "react";
import { sendDM } from "../lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";

export const AskQuestion = () => {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.message.trim()) {
            toast.error("Name and message are required");
            return;
        }
        setSending(true);
        try {
            await sendDM(form);
            setSent(true);
            setForm({ name: "", email: "", message: "" });
            toast.success("Message dropped in the inbox 🙏");
        } catch (err) {
            toast.error("Couldn't send. Try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="ask"
            data-testid="ask-section"
            className="relative py-16 md:py-24"
        >
            <div
                aria-hidden
                className="absolute inset-0 overflow-hidden pointer-events-none"
            >
                <div className="absolute top-10 left-1/3 w-96 h-96 rounded-full bg-hotpink/20 blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-skyblue/20 blur-3xl" />
            </div>

            <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-5 gap-10 items-start">
                <div className="md:col-span-2">
                    <p className="font-graffiti text-kickgreen text-2xl -rotate-2 mb-2">
                        slide in ↘
                    </p>
                    <h2 className="font-anton uppercase text-4xl md:text-6xl text-white leading-none mb-4 stamp-sm">
                        Direct
                        <br />
                        Message
                        <br />
                        Me
                    </h2>
                    <p className="font-chivo text-white/70 text-sm md:text-base leading-relaxed">
                        Got a testimony? Need prayer? Wanna shout out a city? Drop it
                        below. I read every one and answer the realest ones live on
                        stream.
                    </p>
                    <ul className="mt-6 space-y-2 font-chivo text-sm">
                        <li className="flex items-start gap-2">
                            <span className="text-kickgreen font-bold">✓</span>
                            <span className="text-white/80">Private — only I see it</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-hotpink font-bold">✓</span>
                            <span className="text-white/80">
                                Answered live during the 1AM EST show
                            </span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-skyblue font-bold">✓</span>
                            <span className="text-white/80">
                                Add your email if you want a reply back
                            </span>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <form
                        data-testid="dm-form"
                        onSubmit={submit}
                        className="relative bg-surface border-4 border-black brutal-shadow-green p-6 md:p-8 space-y-4"
                    >
                        <div className="absolute -top-3 left-6 bg-hotpink text-white font-anton uppercase text-xs tracking-widest px-3 py-1 border-2 border-black">
                            Ask The Preacher
                        </div>

                        <div>
                            <label className="block font-anton uppercase text-xs tracking-widest text-white/60 mb-2">
                                Your Name *
                            </label>
                            <input
                                data-testid="dm-name-input"
                                value={form.name}
                                onChange={update("name")}
                                placeholder="Brother Mike from Houston"
                                maxLength={80}
                                className="w-full bg-black border-2 border-white/15 px-4 py-3 font-chivo text-white placeholder:text-white/30 focus:border-kickgreen focus:outline-none"
                            />
                        </div>

                        <div>
                            <label className="block font-anton uppercase text-xs tracking-widest text-white/60 mb-2">
                                Email (optional)
                            </label>
                            <input
                                data-testid="dm-email-input"
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                placeholder="you@email.com"
                                maxLength={160}
                                className="w-full bg-black border-2 border-white/15 px-4 py-3 font-chivo text-white placeholder:text-white/30 focus:border-skyblue focus:outline-none"
                            />
                        </div>

                        <div>
                            <label className="block font-anton uppercase text-xs tracking-widest text-white/60 mb-2">
                                Your Message / Question *
                            </label>
                            <textarea
                                data-testid="dm-message-input"
                                value={form.message}
                                onChange={update("message")}
                                rows={5}
                                maxLength={2000}
                                placeholder="What's on your heart?"
                                className="w-full bg-black border-2 border-white/15 px-4 py-3 font-chivo text-white placeholder:text-white/30 focus:border-hotpink focus:outline-none resize-none"
                            />
                            <div className="text-right text-xs text-white/40 font-chivo mt-1">
                                {form.message.length}/2000
                            </div>
                        </div>

                        <button
                            data-testid="dm-submit-btn"
                            disabled={sending}
                            type="submit"
                            className="inline-flex items-center gap-2 bg-kickgreen text-black font-anton uppercase tracking-wider text-lg px-7 py-3 border-2 border-black hover:-translate-y-1 hover:shadow-[6px_6px_0_#FF2E93] transition-all disabled:opacity-60"
                        >
                            <Send className="w-4 h-4" strokeWidth={3} />
                            {sending ? "Sending…" : "Send to Leonard"}
                        </button>

                        {sent && (
                            <p
                                data-testid="dm-success"
                                className="font-graffiti text-skyblue text-xl -rotate-1 pt-2"
                            >
                                preached & delivered ✓
                            </p>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
};
